import { type MicroFrontendModule } from '@platform-services/hrm-mfe-global';

/**
 * Load and mount a micro-frontend
 * @param importKey The import name of the MFE to load
 * @param containerId The DOM element ID to mount the MFE to
 */
export async function loadAndMountMfe(importKey: string, containerId: string): Promise<void> {
  try {
    console.info(`Loading MFE: ${importKey}`);

    // Check if an MFE is already mounted under this key and unmount it first
    const existingEntry = mountedMfes.get(importKey);
    if (existingEntry) {
      console.warn(`MFE ${importKey} is already mounted, unmounting existing instance first`);
      await existingEntry.mfe.unmount();
      mountedMfes.delete(importKey);
    }

    // Dynamic import of the MFE module
    const mfeToLoad = await import(/* @vite-ignore */ importKey);
    const mfeModule = await mfeToLoad.getMfe();

    // Create a fresh instance if factory is available, otherwise use direct export
    const mfe = (
      mfeModule.createMfeInstance ? mfeModule.createMfeInstance() : mfeModule
    ) as MicroFrontendModule;

    // Validate the module
    if (!mfe.mount || typeof mfe.mount !== 'function') {
      throw new Error(`MFE ${importKey} does not export a mount function`);
    }

    if (!mfe.unmount || typeof mfe.unmount !== 'function') {
      throw new Error(`MFE ${importKey} does not export an unmount function`);
    }

    // Bootstrap the MFE if it has a bootstrap function
    if (mfe.bootstrap) {
      await mfe.bootstrap();
    }

    // Mount the MFE to the specified container
    await mfe.mount(containerId, {});
    console.info(`Successfully mounted MFE: ${importKey} to container: ${containerId}`);

    // Store the mounted MFE for potential cleanup
    mountedMfes.set(importKey, { mfe, containerId });
  } catch (error) {
    console.error(`Failed to load or mount MFE: ${importKey}`, error);
  }
}

/**
 * Map to track mounted MFEs for cleanup
 */
const mountedMfes = new Map<string, { mfe: MicroFrontendModule; containerId: string }>();

/**
 * Unmount all loaded MFEs
 */
export async function unmountAllMfes(): Promise<void> {
  for (const [name, { mfe, containerId }] of mountedMfes.entries()) {
    try {
      await mfe.unmount(containerId);
      console.info(`Unmounted MFE: ${name}`);
    } catch (error) {
      console.error(`Failed to unmount MFE: ${name}`, error);
    }
  }

  mountedMfes.clear();
}

/**
 * Unmount a specific MFE by its import key
 */
export async function unmountMfe(name: string): Promise<void> {
  const entry = mountedMfes.get(name);
  if (entry) {
    try {
      await entry.mfe.unmount(entry.containerId);
      mountedMfes.delete(name);
      console.info(`Unmounted MFE: ${name}`);
    } catch (error) {
      mountedMfes.delete(name);
      console.error(`Failed to unmount MFE: ${name}`, error);
    }
  } else {
    console.warn(`Cannot unmount MFE ${name}: not found in mounted MFEs`);
  }
}
